import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventForCutoff } from '../api/events';
import { useEventSearch } from '../hooks/queries/useEventSearch';
import {
  useMyRsvps,
  usePatchMyRsvps,
  useInvalidateMyRsvps,
  myRsvpMap,
} from '../hooks/queries/useMyRsvps';
import { queryKeys } from '../lib/query/queryKeys';
import { patchGoingCountInInfiniteFeed, computeRsvpGoingDelta } from '../lib/query/cachePatches';
import { invalidateAfterRsvpChange } from '../lib/query/invalidateMutations';
import { useToast } from '../contexts/ToastContext';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useEventRsvpParity } from '../hooks/useEventRsvpParity';
import EventFeedResultsGrid from './EventFeedResultsGrid';
import SearchChrome from './search/SearchChrome';
import { filterDisplayOpenEvents, filterEventsBySearchQuery } from '../utils/eventFeedModel';
import {
  saveEventsSearchScroll,
  restoreEventsSearchScroll,
  attachMainScrollPersistence,
} from '../lib/eventsScrollStorage';
import { isSearchInFlight, shouldShowSearchEmpty } from '../utils/searchUiState';
import { useCancelledEvents, withoutCancelled } from '../hooks/queries/useCancelledEvents';

const __DEBUG_PAGINATION__ = import.meta.env.DEV;

export default function EventSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { session, authReady } = useCurrentUserProfile();
  const queryClient = useQueryClient();

  const queryFromUrl = searchParams.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(queryFromUrl);
  const [debouncedQuery, setDebouncedQuery] = useState(queryFromUrl.trim());

  const [error, setError] = useState(null);

  const observerTarget = useRef(null);
  const lastObserverFireRef = useRef(0);
  const eventsRef = useRef([]);
  const rsvpsRef = useRef([]);
  const loadMoreInFlight = useRef(false);
  const didRestoreScrollRef = useRef(false);
  const searchInputRef = useRef(null);

  // Desktop uses inline search on the feed — this route is mobile-only.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const redirectDesktop = () => {
      if (mq.matches) navigate('/', { replace: true });
    };
    redirectDesktop();
    mq.addEventListener('change', redirectDesktop);
    return () => mq.removeEventListener('change', redirectDesktop);
  }, [navigate]);

  useEffect(() => {
    setSearchInput(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const current = searchParams.get('q') ?? '';
    if (trimmed === current) return;
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }, [searchInput, searchParams, setSearchParams]);

  useLayoutEffect(() => {
    didRestoreScrollRef.current = restoreEventsSearchScroll(queryFromUrl);
  }, []);

  useEffect(() => {
    if (didRestoreScrollRef.current) {
      didRestoreScrollRef.current = false;
      return;
    }
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [debouncedQuery]);

  useEffect(() => {
    return attachMainScrollPersistence(() => saveEventsSearchScroll(debouncedQuery));
  }, [debouncedQuery]);

  const {
    events,
    isPending: searchPending,
    isFetching: searchFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: searchQueryError,
  } = useEventSearch({
    query: debouncedQuery,
    userId: session?.user?.id,
    authReady,
  });

  const loading = searchPending && events.length === 0;
  const loadingMore = isFetchingNextPage;
  const hasMore = Boolean(hasNextPage);
  const searchInFlight = isSearchInFlight({
    input: searchInput,
    debouncedQuery,
    isLoading: loading,
    isFetching: searchFetching && !isFetchingNextPage,
  });

  const { data: rsvps = [] } = useMyRsvps(session?.user?.id, { enabled: authReady });
  const patchMyRsvps = usePatchMyRsvps(session?.user?.id);
  const invalidateMyRsvps = useInvalidateMyRsvps();

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    rsvpsRef.current = rsvps;
  }, [rsvps]);

  useEffect(() => {
    if (searchQueryError) {
      setError(searchQueryError.message || 'Failed to search events.');
    }
  }, [searchQueryError]);

  const refetchRsvps = useCallback(async () => {
    invalidateMyRsvps(session?.user?.id);
    invalidateAfterRsvpChange(queryClient, { userId: session?.user?.id });
  }, [invalidateMyRsvps, session?.user?.id, queryClient]);

  const getEventForCutoff = useCallback(
    async (eventId) => {
      const fromResults = events.find((event) => event.event_id === eventId);
      if (fromResults) return fromResults;
      return fetchEventForCutoff(eventId);
    },
    [events],
  );

  const updateLocalRsvp = useCallback(
    (eventId, newStatus) => {
      const oldR = rsvpsRef.current.find((r) => r.event_id === eventId);
      const delta = computeRsvpGoingDelta(oldR?.status, newStatus);
      patchMyRsvps(eventId, newStatus);
      if (delta !== 0) {
        patchGoingCountInInfiniteFeed(
          queryClient,
          queryKeys.events.search(debouncedQuery, session?.user?.id),
          eventId,
          delta,
        );
      }
    },
    [patchMyRsvps, queryClient, debouncedQuery, session?.user?.id],
  );

  const { handleEventCardRSVP, parityModals } = useEventRsvpParity({
    session,
    showToast,
    getEventForCutoff,
    updateLocalRsvp,
    refetchRsvps,
  });

  const loadMore = useCallback(async () => {
    if (
      !hasNextPage ||
      isFetchingNextPage ||
      loading ||
      loadMoreInFlight.current ||
      !debouncedQuery
    )
      return;
    loadMoreInFlight.current = true;
    try {
      await fetchNextPage();
    } catch (err) {
      setError(err.message || 'Failed to load more.');
    } finally {
      loadMoreInFlight.current = false;
    }
  }, [hasNextPage, isFetchingNextPage, loading, fetchNextPage, debouncedQuery]);

  const lastEventElementRef = useCallback(
    (node) => {
      if (loadingMore || !hasMore) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      observerTarget.current = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        const now = Date.now();
        if (now - lastObserverFireRef.current < 600) return;
        lastObserverFireRef.current = now;
        loadMore();
      });
      if (node) observerTarget.current.observe(node);
    },
    [loadingMore, hasMore, loadMore],
  );

  useEffect(() => {
    return () => {
      if (observerTarget.current) observerTarget.current.disconnect();
    };
  }, []);

  const userRsvpByEvent = useMemo(() => myRsvpMap(rsvps), [rsvps]);

  // Searching for a cancelled show should not find one either - see
  // api/events.fetchCancelledEventIds for why the RPC cannot tell us.
  const cancelled = useCancelledEvents();
  const filteredEvents = useMemo(() => {
    const open = withoutCancelled(filterDisplayOpenEvents(events), cancelled);
    return filterEventsBySearchQuery(open, debouncedQuery);
  }, [events, debouncedQuery, cancelled]);

  const showNoResults = shouldShowSearchEmpty({
    input: searchInput,
    debouncedQuery,
    isLoading: loading,
    isFetching: searchFetching && !isFetchingNextPage,
    resultCount: filteredEvents.length,
  });

  const handleBack = () => {
    saveEventsSearchScroll(debouncedQuery);
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleEventNavigate = (eventId) => {
    saveEventsSearchScroll(debouncedQuery);
    navigate(`/events/${eventId}`);
  };

  const handleRSVP = async (eventId, status, myStatus) => {
    await handleEventCardRSVP(eventId, status, myStatus ?? null);
  };

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SearchChrome
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchInputRef={searchInputRef}
          onBack={handleBack}
          placeholder="Artists, venues, events"
          backLabel="Back to Events"
          onClear={() => {
            setSearchInput('');
            searchInputRef.current?.focus();
          }}
        />
        <div className="rounded-lg bg-danger-fill text-danger p-4 mt-4">{error}</div>
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SearchChrome
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchInputRef={searchInputRef}
          onBack={handleBack}
          placeholder="Artists, venues, events"
          backLabel="Back to Events"
          onClear={() => {
            setSearchInput('');
            searchInputRef.current?.focus();
          }}
        />

        <div className="mt-4">
          {!searchInput.trim() ? (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-dark-muted">
              <p className="text-base">Start typing to find events.</p>
            </div>
          ) : searchInFlight && filteredEvents.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-dark-muted py-12">Searching...</p>
          ) : showNoResults ? (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-dark-muted">
              <p className="text-base">{`No events match "${debouncedQuery}".`}</p>
              <p className="text-sm mt-2">Try a different artist or venue.</p>
            </div>
          ) : (
            <EventFeedResultsGrid
              events={filteredEvents}
              userRsvpByEvent={userRsvpByEvent}
              hasMore={hasMore}
              loadingMore={loadingMore}
              joinSource="search-result"
              onEventNavigate={handleEventNavigate}
              onRSVP={handleRSVP}
              lastEventElementRef={lastEventElementRef}
            />
          )}
        </div>
      </div>

      {parityModals}
    </div>
  );
}
